import React from 'react';
import { Calendar, Calculator, Edit, Trash2 } from 'lucide-react';
import { ModalityBadge, StatusBadge, PriorityBadge } from './Badges';
import PatientAvatar from './PatientAvatar';
import WorklistSkeleton from "../../../shared/custom-ui/skeleton/Worklist/WorklistSkeleton"

const Icon = { Calendar, Calculator, Edit, Trash2 } as any;

interface WorklistLayoutProps {
    data: any[];
    loading?: boolean;
    onRowClick?: (row: any) => void;
    onEdit?: (row: any) => void;
    onDelete?: (row: any) => void;
    scale?: number;
}

const WorklistLayout: React.FC<WorklistLayoutProps> = ({
    data,
    loading = false,
    onRowClick,
    onEdit,
    onDelete,
    scale = 1,
}) => {

    if (loading) {
        return <WorklistSkeleton />;
    }

    if (!data || data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-24 h-24 mb-6 rounded-full flex items-center justify-center" style={{ background: 'var(--v2-bg-card-inner)' }}>
                    <div className="w-12 h-12" style={{ color: 'var(--v2-text-muted)' }}>
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--v2-text-heading)' }}>No tasks found</h3>
                <p className="max-w-md mx-auto" style={{ color: 'var(--v2-text-muted)' }}>
                    Your worklist is currently empty. New studies will appear here when they are assigned or scheduled.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 pb-4">
            {/* Desktop Header — scaled */}
            <div className="hidden lg:block" style={{ zoom: scale }}>
                {/* Header Row */}
                <div className="depth-table-header grid grid-cols-[50px_minmax(140px,1.6fr)_minmax(80px,0.9fr)_minmax(80px,0.9fr)_minmax(100px,1fr)_minmax(110px,1.1fr)_minmax(120px,1.3fr)_minmax(90px,0.9fr)_60px_40px_70px] gap-2 px-5 py-3.5 text-[14px] font-extrabold uppercase tracking-wider whitespace-nowrap">
                    <div>&nbsp;</div> {/* Avatar */}
                    <div>Patient / MRN</div>
                    <div>Procedure</div>
                    <div>AI Priority</div>
                    <div>Priority</div>
                    <div>Status</div>
                    <div>Radiologist</div>
                    <div>Date</div>
                    <div className="text-center">Elapse</div>
                    <div className="text-center">Img</div>
                    <div className="text-center">Actions</div>
                </div>
            </div>

            {/* Data Rows - Desktop grid — scaled */}
            <div className="hidden lg:flex lg:flex-col lg:gap-3" style={{ zoom: scale }}>
                {data.map((row, index) => (
                    <div
                        key={row.PK || index}
                        onClick={() => onRowClick?.(row)}
                        className="group relative rounded-xl cursor-pointer overflow-hidden transition-all duration-200"
                        style={{
                            background: 'var(--v2-bg-card)',
                            border: '1px solid var(--v2-border-card)',
                            boxShadow: 'var(--v2-shadow-card)',
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.boxShadow = 'var(--v2-shadow-card-hover)';
                            e.currentTarget.style.borderColor = 'var(--v2-border-glass-strong)';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.boxShadow = 'var(--v2-shadow-card)';
                            e.currentTarget.style.borderColor = 'var(--v2-border-card)';
                        }}
                    >
                        <div className="p-4 grid grid-cols-[50px_minmax(140px,1.6fr)_minmax(80px,0.9fr)_minmax(80px,0.9fr)_minmax(100px,1fr)_minmax(110px,1.1fr)_minmax(120px,1.3fr)_minmax(90px,0.9fr)_60px_40px_70px] gap-2 items-center">

                            {/* 1. Avatar Column */}
                            <div>
                                <PatientAvatar name={row.patient_name} mrn={row.mrn_number} />
                            </div>

                            {/* 2. Patient Name + MRN */}
                            <div className="overflow-hidden">
                                <h4 className="font-semibold text-[15px] truncate whitespace-nowrap text-[var(--v2-text-primary)]" style={{ color: 'var(--v2-text-primary)' }}>{row.patient_name || 'Unknown Patient'}</h4>
                                <span className="text-[15px] font-semibold whitespace-nowrap" style={{ color: 'var(--v2-text-muted)' }}>MRN: {row.mrn_number || 'N/A'}</span>
                            </div>

                            {/* 3. Procedure */}
                            <div>
                                <div className="flex items-center gap-1.5">
                                    <ModalityBadge modality={row.Procedure?.split(' ')[0] || 'N/A'} />
                                    <span className="hidden xl:inline text-[15px] font-semibold truncate max-w-[90px]" style={{ color: 'var(--v2-text-muted)' }} title={row.Procedure}>
                                        {row.Procedure?.split(' ').slice(1).join(' ') || ''}
                                    </span>
                                </div>
                            </div>

                            {/* 4. AI Priority */}
                            <div>
                                <PriorityBadge priority={row.ai_priority} />
                            </div>

                            {/* 5. Study Priority */}
                            <div className="overflow-hidden">
                                <PriorityBadge priority={row.study_priority} />
                            </div>

                            {/* 6. Status */}
                            <div className="overflow-hidden">
                                <StatusBadge status={row.status} />
                            </div>

                            {/* 7. Radiologist */}
                            <div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[15px] font-semibold truncate" style={{ color: 'var(--v2-text-primary)' }}>
                                        {row.radiologist_name || 'Unassigned'}
                                    </span>
                                </div>
                            </div>

                            {/* 8. Study Date */}
                            <div>
                                <span className="text-[15px] font-semibold flex items-center gap-1" style={{ color: 'var(--v2-text-primary)' }}>
                                    {row.StudyDate || new Date(row.assigned_at).toLocaleDateString()}
                                </span>
                            </div>

                            {/* 9. Elapse Time */}
                            <div className="flex items-center justify-center">
                                <span className="text-[15px] font-semibold flex items-center gap-1" style={{ color: 'var(--v2-text-muted)' }}>
                                    {row.elapse_time || '0m'}
                                </span>
                            </div>

                            {/* 10. Images */}
                            <div className="flex items-center justify-center">
                                <span className="text-[15px] font-semibold" style={{ color: 'var(--v2-text-muted)' }}>
                                    {row.Images || 0}
                                </span>
                            </div>

                            {/* 11. Actions */}
                            <div className="flex items-center justify-center gap-1">
                                {onEdit && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(row);
                                        }}
                                        className="p-1.5 text-[var(--v2-text-muted)] hover:text-blue-600 hover:bg-blue-50/80 rounded-lg transition-colors"
                                        title="Edit"
                                    >
                                        <Icon.Edit className="w-4 h-4" />
                                    </button>
                                )}
                                {onDelete && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(row);
                                        }}
                                        className="p-1.5 text-[var(--v2-text-muted)] hover:text-red-600 hover:bg-red-50/80 rounded-lg transition-colors"
                                        title="Delete"
                                    >
                                        <Icon.Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                        </div>

                        {/* Active/Status Indicator Line */}
                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${row.status === 'COMPLETED' ? 'bg-emerald-500' :
                            row.status === 'IN_PROGRESS' ? 'bg-blue-500' :
                                (row.ai_priority === 'URGENT' || row.study_priority === 'CRITICAL') ? 'bg-red-500' : 'bg-transparent'
                            }`} />
                    </div>
                ))}
            </div>

            {/* ══════ MOBILE CARD LAYOUT — Slim compact cards ══════ */}
            <div className="lg:hidden flex flex-col gap-3">
                {data.map((row, index) => {
                    const accentColor =
                        row.status === 'COMPLETED' ? '#10b981' :
                            row.status === 'IN_PROGRESS' ? '#3b82f6' :
                                row.status === 'ON_HOLD' ? '#f59e0b' :
                                    (row.study_priority === 'CRITICAL' || row.ai_priority === 'URGENT') ? '#ef4444' :
                                        '#818cf8';

                    return (
                        <div
                            key={row.PK || index}
                            onClick={() => onRowClick?.(row)}
                            className="relative rounded-xl cursor-pointer overflow-hidden transition-all duration-200 active:scale-[0.99]"
                            style={{
                                background: 'var(--v2-bg-card)',
                                border: '1px solid var(--v2-border-card)',
                                borderLeft: `3px solid ${accentColor}`,
                                boxShadow: '0 2px 8px -2px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.05)',
                            }}
                        >
                            {/* Row 1: Avatar + Name + Status */}
                            <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2.5">
                                <div className="flex items-center gap-2.5 min-w-0">
                                    <div
                                        className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-[13px] font-black text-white"
                                        style={{ background: accentColor }}
                                    >
                                        {row.patient_name ? row.patient_name.charAt(0).toUpperCase() : '?'}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-[14px] leading-tight truncate" style={{ color: 'var(--v2-text-primary)' }}>
                                            {row.patient_name || 'Unknown Patient'}
                                        </p>
                                        <p className="text-[11px] font-medium" style={{ color: 'var(--v2-text-muted)' }}>
                                            MRN: {row.mrn_number || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                <StatusBadge status={row.status} />
                            </div>

                            {/* Row 2: Priority pills inline */}
                            <div
                                className="flex items-center gap-2 px-4 py-2 flex-wrap"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)' }}
                            >
                                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--v2-text-muted)' }}>AI:</span>
                                <PriorityBadge priority={row.ai_priority} />
                                <span className="text-[var(--v2-border-card)] select-none">·</span>
                                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--v2-text-muted)' }}>Priority:</span>
                                <PriorityBadge priority={row.study_priority} />
                                <span className="text-[var(--v2-border-card)] select-none">·</span>
                                <ModalityBadge modality={row.Procedure?.split(' ')[0] || 'N/A'} />
                            </div>

                            {/* Row 3: Radiologist + date + meta */}
                            <div
                                className="flex items-center justify-between gap-3 px-4 py-2.5"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)', background: 'var(--v2-bg-card-inner)' }}
                            >
                                <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                                    <div
                                        className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-bold"
                                        style={{ background: 'var(--v2-bg-hover)', color: 'var(--v2-text-secondary)' }}
                                    >
                                        {row.radiologist_name ? row.radiologist_name.charAt(0) : '?'}
                                    </div>
                                    <span className="text-[12px] font-medium truncate" style={{ color: 'var(--v2-text-primary)' }}>
                                        {row.radiologist_name || 'Unassigned'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-[11px] font-medium flex-shrink-0" style={{ color: 'var(--v2-text-muted)' }}>
                                    <Icon.Calendar className="w-3 h-3" style={{ color: accentColor, opacity: 0.85 }} />
                                    <span>{row.StudyDate || new Date(row.assigned_at).toLocaleDateString()}</span>
                                    <span>·</span>
                                    <span>{row.elapse_time || '0m'}</span>
                                </div>
                            </div>

                            {/* Row 4: Actions */}
                            <div
                                className="flex items-center justify-end gap-1.5 px-4 py-2"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)' }}
                            >
                                {onEdit && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onEdit(row); }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg transition-all duration-150 active:scale-95"
                                        style={{
                                            color: 'var(--v2-text-secondary)',
                                            background: 'var(--v2-bg-card)',
                                            border: '1px solid var(--v2-border-subtle)',
                                        }}
                                    >
                                        <Icon.Edit className="w-3.5 h-3.5" />
                                        Edit
                                    </button>
                                )}
                                {onDelete && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onDelete(row); }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg transition-all duration-150 active:scale-95"
                                        style={{
                                            color: '#ef4444',
                                            background: 'rgba(239,68,68,0.07)',
                                            border: '1px solid rgba(239,68,68,0.15)',
                                        }}
                                    >
                                        <Icon.Trash2 className="w-3.5 h-3.5" />
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WorklistLayout;
